import { Card } from '@/components/common/card'
import { getBorrowMarketCollateralAsset } from '@/utils/borrowMarkets/getBorrowMarketCollateralAsset'
import { getSortedBorrowMarketCollateralPairs } from '@/utils/borrowMarkets/getSortedBorrowMarketCollateralPairs'
import type { BorrowMarketAssetData, BorrowMarketDetail } from '@/utils/internalAppSdk'
import { formatDecimalToPercentage } from '@vaultsfyi/common/utils'
import { useMemo, useState } from 'react'
import { BorrowMarketCollateralSelect } from './borrowMarketCollateralSelect'
import { BorrowMarketParameterRow } from './borrowMarketParameterRow'

interface Props {
  market: BorrowMarketDetail
  borrowAssetData: BorrowMarketAssetData
}

export const BorrowMarketParameters = ({ market, borrowAssetData }: Props) => {
  const collateralPairs = useMemo(
    () => getSortedBorrowMarketCollateralPairs({ market, borrowAssetData }),
    [market.pairs, borrowAssetData.marketAssetId]
  )
  const [selectedCollateralMarketAssetId, setSelectedCollateralMarketAssetId] = useState(
    collateralPairs[0]?.collateralMarketAssetId
  )
  const selectedPair =
    collateralPairs.find((pair) => pair.collateralMarketAssetId === selectedCollateralMarketAssetId) ??
    collateralPairs[0]
  const collateralAsset = getBorrowMarketCollateralAsset(market, selectedPair)

  return (
    <Card className="flex flex-col gap-2 bg-surface p-5">
      <div className="flex w-full items-center justify-between gap-4">
        <h2 className="font-medium text-base text-heading-foreground">Market parameters</h2>
        {selectedPair && collateralAsset && (
          <BorrowMarketCollateralSelect
            pairs={collateralPairs}
            market={market}
            selectedPair={selectedPair}
            onSelect={setSelectedCollateralMarketAssetId}
          />
        )}
      </div>
      <dl className="flex w-full flex-col gap-0.5">
        <BorrowMarketParameterRow
          label="Borrow enabled"
          value={selectedPair?.borrowEnabled ? 'Yes' : 'No'}
          valueClassName={selectedPair?.borrowEnabled ? 'text-success' : undefined}
        />
        <BorrowMarketParameterRow
          label="Max LTV"
          value={selectedPair ? formatDecimalToPercentage(selectedPair.riskParameters.maxLtv) : '--'}
        />
        <BorrowMarketParameterRow
          label="Liquidation threshold"
          value={selectedPair ? formatDecimalToPercentage(selectedPair.riskParameters.liquidationThreshold) : '--'}
        />
        <BorrowMarketParameterRow
          label="Liquidation penalty"
          value={
            selectedPair?.riskParameters.liquidationPenalty !== undefined
              ? formatDecimalToPercentage(selectedPair.riskParameters.liquidationPenalty)
              : '--'
          }
        />
      </dl>
    </Card>
  )
}
